import { Set } from "immutable";
import { each, filter, find, has, isArray, isNil, isObject, isString, keys, map, reduce } from "lodash";

import { OUTLINE_LABEL_COLUMN_WIDTH, OUTLINE_MIN_COLUMN_WIDTH, OUTLINE_NARRATIVE_COLUMN_WIDTH } from "../constants";
import compactDisplays from "../constants/compactDisplay";
import { ConstraintTypes } from "../constants/constraints";
import defaultExposedFilterTypes from "../constants/defaultExposedFilterTypes";
import defaultFictionNarrativeLabels from "../constants/defaultFictionNarrativeLabels";
import itemTypeCategories from "../constants/itemTypeCategories";
import itemTypeIds from "../constants/itemTypeIds";
import outlineColumnIds from "../constants/outlineColumnIds";
import { PropertyFormatIdentifier } from "../constants/propertyFormats";
import { REFERENCE_RELATES_TO } from "../constants/references";
import subwayDisplayOptions from "../constants/subwayDisplayOptions";
import { isDateValid } from "../modules/definition/calendar/dateFunctions";
import dateWithComponents from "../modules/definition/calendar/dateFunctions/dateWithComponents";
import { wasOldStyleGregorianCalendarFromCalendar } from "../modules/definition/calendar/gregorianCalendar";
import { createFromJS } from "../types";
import treeUtils from "../utils/tree";
import uniqueId from "../utils/uniqueId";

export const relatesToRefObject = {
  enabled: true,
  canDelete: false,
  id: REFERENCE_RELATES_TO,
  key: null,
  label: "Relates to",
  types: [],
  icon: "circle",
  color: "mono",
  lineStyle: "lineStyle_SOLID",
  multiple: true,
  bidirectional: true,
  inverseLabel: null,
  showInverse: false,
  showInInspector: false
};

export const narrativeFolderObject = {
  enabled: false,
  canDelete: false,
  id: itemTypeIds.NARRATIVE_FOLDER_TYPE_ID,
  label: "Narrative Folder",
  pluralLabel: "Narrative Folders",
  labelDisplayLabel: "Label",
  startDisplayLabel: "Start",
  endDisplayLabel: "End",
  references: [],
  properties: [],
  superTypes: [],
  icon: "folder",
  color: "iconMono",
  canHaveDate: false,
  canIncludeInNarrative: true,
  showInSequence: false,
  showOnSidebar: false,
  showAsColumn: false,
  identifierPrefix: "NF",
  isRelative: false,
  relativeUnits: "seconds",
  defaultSequenceType: false,
  defaultNarrativeType: false,
  isNarrativeFolder: true
};

const renameProp = (oldProp, newProp, { [oldProp]: old, ...others }) => ({
  [newProp]: old,
  ...others
});

const fixSequenceOptions = (result, keyOfViews) => {
  const appState = result.appState;
  if (appState[keyOfViews] && appState[keyOfViews].byId) {
    const views = appState[keyOfViews].byId;
    Object.keys(views).forEach(key => {
      const view = views[key];
      if (view.sequenceOptions) {
        views[key] = renameProp("sequenceOptions", "subwayOptions", view);
      }
    });
  }
};

const fixColumnWidths = columns => {
  return columns.map(c => {
    let newC = Object.assign({}, c);
    newC.width = Math.max(OUTLINE_MIN_COLUMN_WIDTH, newC.width);
    return newC;
  });
};

const fixOutlineColumnsLabelAndNarrative = columns => {
  const labelCol = {
    id: outlineColumnIds.label,
    width: OUTLINE_LABEL_COLUMN_WIDTH
  };
  const narrativeCol = {
    id: outlineColumnIds.narrative,
    width: OUTLINE_NARRATIVE_COLUMN_WIDTH
  };
  // Ensure "label" and "narrative" are first and second items
  // If we don't have two columns, return the two mandatory columns
  if (columns.length < 2) {
    return [labelCol, narrativeCol];
  }
  // If label and narrative are not the first two, remove them from the list and add them at the
  // front.
  if (columns[0].id !== outlineColumnIds.label || columns[1].id !== outlineColumnIds.narrative) {
    let newColumns = filter(columns, c => c.id !== outlineColumnIds.label && c.id !== outlineColumnIds.narrative);
    newColumns.unshift(narrativeCol);
    newColumns.unshift(labelCol);
    return newColumns;
  }
  return columns;
};

const fixOutlineColumns = columns => {
  let newColumns = columns;
  newColumns = fixOutlineColumnsLabelAndNarrative(newColumns);
  newColumns = fixColumnWidths(newColumns);
  return newColumns;
};

const fixSpreadsheetColumns = columns => {
  let newColumns = columns;
  newColumns = fixColumnWidths(newColumns);
  return newColumns;
};

const fixViewColumns = (result, keyOfViews) => {
  const appState = result.appState;
  if (appState[keyOfViews] && appState[keyOfViews].byId) {
    const views = appState[keyOfViews].byId;
    Object.keys(views).forEach(key => {
      let view = views[key];
      // If the outlineOptions has columns, we need to update them.
      // Otherwise, the default values provided by the Record will be fine.
      if (view.outlineOptions && view.outlineOptions.columns) {
        view.outlineOptions.columns = fixOutlineColumns(view.outlineOptions.columns);
      }
      if (view.spreadsheetOptions && view.spreadsheetOptions.columns) {
        view.spreadsheetOptions.columns = fixSpreadsheetColumns(view.spreadsheetOptions.columns);
      }
    });
  }
};

const setRelationshipViewColumnTypeIds = (obj, keyOfViews) => {
  if (!obj.appState || !obj.data || !obj.data.items || !obj.definitions || !obj.definitions.types) {
    return;
  }

  const appState = obj.appState;
  const itemsById = obj.data.items.byId;
  const typesById = obj.definitions.types.byId;
  if (appState[keyOfViews] && appState[keyOfViews].byId) {
    const views = appState[keyOfViews].byId;
    Object.keys(views).forEach(key => {
      let view = views[key];
      if (view.relationshipOptions && view.relationshipOptions.columnIds && !view.relationshipOptions.typeIds) {
        let typeIds = [];
        view.relationshipOptions.columnIds.map(cId => {
          const item = itemsById[cId];
          if (item) {
            const typeId = item.type;
            const type = typesById[typeId];
            if (!typeIds.includes(typeId) && type && type.showAsColumn) {
              typeIds.push(typeId);
            }
          }
        });
        view.relationshipOptions.typeIds = typeIds;
      }
    });
  }
};

const removeSubwayViewAllEventsThread = (obj, keyOfViews) => {
  const appState = obj.appState;
  if (appState[keyOfViews] && appState[keyOfViews].byId) {
    const views = appState[keyOfViews].byId;
    Object.keys(views).forEach(key => {
      let view = views[key];
      if (view.subwayOptions && view.subwayOptions.threadIds) {
        const index = view.subwayOptions.threadIds.indexOf(subwayDisplayOptions.SHOW_ALL);
        if (index > -1) {
          view.subwayOptions.threadIds = view.subwayOptions.threadIds.splice(index, 1);
        }
      }
    });
  }
};

const removeInvalidThreads = (obj, keyOfViews) => {
  const appState = obj.appState;
  if (appState[keyOfViews] && appState[keyOfViews].byId) {
    const views = appState[keyOfViews].byId;
    const itemsById = obj.data && obj.data.items && obj.data.items.byId ? obj.data.items.byId : {};
    Object.keys(views).forEach(key => {
      let view = views[key];
      if (view.subwayOptions && view.subwayOptions.threadIds) {
        view.subwayOptions.threadIds = view.subwayOptions.threadIds.filter(tId => itemsById[tId]);
      }
    });
  }
};

const addExposedFilterTypes = obj => {
  const appState = obj.appState;
  if (!appState.exposedFilterTypes) {
    appState.exposedFilterTypes = defaultExposedFilterTypes;
  }
};

const createGlobalTags = result => {
  if (!result.data || !result.data.items) {
    return;
  }

  let tagsToIds = {};
  let tagMap = {};

  each(result.data.items.byId, item => {
    const itemTags = item.tags ? item.tags : [];
    let itemTagIds = [];

    each(itemTags, tag => {
      if (!tagsToIds[tag]) {
        const tagId = uniqueId();
        tagsToIds[tag] = tagId;
        tagMap[tagId] = tag;
      }
      itemTagIds.push(tagsToIds[tag]);
    });
    item.tags = itemTagIds;
  });

  result.data.tags = tagMap;
};

const getChildrenIdsForId = (itemsById, id) =>
  reduce(
    itemsById,
    (result, item) => {
      if (item.super === id) {
        result.push(item.id);
      }
      return result;
    },
    []
  );

const setItemChildren = result => {
  if (!result.data || !result.data.items) {
    return;
  }
  // Iterate through all items
  // If they do not have children set
  // Then we need to calculate the children list (the first time)
  // and then set the children for each itme
  each(result.data.items.byId, item => {
    // Note: as per the comment below in the main function, we now do this
    // for every item if this function gets called, because the data became
    // somehow invalid previously.
    item.children = getChildrenIdsForId(result.data.items.byId, item.id);
  });
};

const appendSuperAndChildrenByIdToData = result => {
  // If the value is already set, just use that...
  if (!result.data || !result.data.items) {
    return;
  }
  if (result.superAndChildrenById) {
    return;
  }
  // Otherwise, we need to copy it from children
  let superAndChildrenById = {};
  each(result.data.items.byId, item => {
    superAndChildrenById[item.id] = {
      super: item.super,
      children: item.children
    };
  });
  result.data.superAndChildrenById = superAndChildrenById;
};

const convertAllowedPropertyValuesToMap = obj => {
  // Iterate through all properties and find any where allowed is set to an
  // array instead of a map
  if (!obj.definitions || !obj.definitions.properties) {
    return;
  }

  each(obj.definitions.properties.byId, prop => {
    if (prop.format === PropertyFormatIdentifier.list) {
      prop.allowed = isArray(prop.allowed)
        ? map(prop.allowed, value => {
          return isObject(value)
            ? value
            : {
              id: uniqueId(),
              label: value
            };
        })
        : [];

      if (obj.data && obj.data.items) {
        // Now, go through all items and update any of their property values to use the
        // option ID instead of the value...
        each(obj.data.items.byId, item => {
          if (item.propertyValues && item.propertyValues[prop.id]) {
            const val = item.propertyValues[prop.id];
            let obj = find(prop.allowed, opt => opt.id === val);
            let obj2 = obj ? obj : find(prop.allowed, opt => opt.label === val);

            if (isNil(obj2)) {
              delete item.propertyValues[prop.id];
            } else {
              item.propertyValues[prop.id] = obj2.id;
            }
          }
        });
      }
    }
  });
};

const setPreferredPrecision = obj => {
  if (!obj.data || !obj.data.items) {
    return;
  }
  each(obj.data.items.byId, item => {
    if (isNil(item.preferredPrecision) && !isNil(item.preferredStartDate)) {
      item.preferredPrecision = item.preferredStartDate.precision;
    }
  });
};

// Convert Narrative Item/Folder to be of type Folder
const ensureNarrativeFolderHasCorrectSetting = obj => {
  if (obj.definitions && obj.definitions.types.byId[itemTypeIds.NARRATIVE_FOLDER_TYPE_ID]) {
    obj.definitions.types.byId[itemTypeIds.NARRATIVE_FOLDER_TYPE_ID].id = itemTypeIds.NARRATIVE_FOLDER_TYPE_ID;
    obj.definitions.types.byId[itemTypeIds.NARRATIVE_FOLDER_TYPE_ID].isNarrativeFolder = true;
    obj.definitions.types.byId[itemTypeIds.NARRATIVE_FOLDER_TYPE_ID].canIncludeInNarrative = true;
  }
  if (obj.definitions && obj.definitions.types.byId["narrativeFolder"]) {
    obj.definitions.types.byId["narrativeFolder"].isNarrativeFolder = true;
  }
  // Also update some of the older variants
  if (obj.definitions && obj.definitions.types.byId["narrativeFolder"]) {
    obj.definitions.types.byId["narrativeFolder"].isNarrativeFolder = true;
  }
  if (obj.definitions && obj.definitions.types.byId["narrativePart"]) {
    obj.definitions.types.byId["narrativePart"].isNarrativeFolder = true;
    // And rename "Narrative Part" to "Narrative Folder"...
    if (obj.definitions.types.byId["narrativePart"].label === "Narrative Part") {
      obj.definitions.types.byId["narrativePart"].label = "Narrative Folder";
      obj.definitions.types.byId["narrativePart"].pluralLabel = "Narrative Folders";
      obj.definitions.types.byId["narrativePart"].icon = "folder";
    }
  }
};

const ensureNarrativeFolderExists = data => {
  if (!data.definitions || !data.definitions.types) {
    return;
  }

  // Include Narrative defaults to true when converted to Immutable Records anyway
  // so we need to make that change here so that we are acting reliably on that variable
  // below.
  if (isNil(data.definitions.includeNarrative)) {
    data.definitions.includeNarrative = true;
  }

  let folderType = Object.values(data.definitions.types.byId).find(type => type.isNarrativeFolder);
  if (!folderType) {
    data.definitions.types.allIds.push(itemTypeIds.NARRATIVE_FOLDER_TYPE_ID);
    data.definitions.types.byId[itemTypeIds.NARRATIVE_FOLDER_TYPE_ID] = narrativeFolderObject;
    folderType = data.definitions.types.byId[itemTypeIds.NARRATIVE_FOLDER_TYPE_ID];
  }

  // The narrative folder shouldn't be the default narrative type or deletable
  // It should be enabled/disabled depending on if the narrative is allowed
  folderType.defaultNarrativeType = false;
  folderType.canDelete = false;
  folderType.enabled = data.definitions.includeNarrative;
};

// Same as the one in type selectors.
// Just avoid any possible issues (Eg. Record vs js object)
const isNarrativeOnlyType = type =>
  !type.canHaveDate && !type.showInSequence && !type.showOnSidebar && type.canIncludeInNarrative;

const ensureIncludeNarrativeIsCorrect = data => {
  if (!data.definitions || !data.definitions.types || !data.data || !data.data.items || !data.data.items.byId) {
    return;
  }
  const hasItemWithNarrativeOnlyType = Object.values(data.data.items.byId).find(i => {
    const itemType = data.definitions.types.byId[i.type];
    return isNarrativeOnlyType(itemType);
  });
  if (hasItemWithNarrativeOnlyType !== undefined) {
    data.definitions.includeNarrative = true;
  }
};

const ensureRelatesToRefExists = data => {
  if (!data.definitions || !data.definitions.references) {
    return;
  }

  if (data.definitions.references.allIds.indexOf(REFERENCE_RELATES_TO) === -1) {
    data.definitions.references.allIds.push(REFERENCE_RELATES_TO);
    data.definitions.references.byId[REFERENCE_RELATES_TO] = relatesToRefObject;
  }

  if (data.definitions.references.byId[REFERENCE_RELATES_TO]) {
    data.definitions.references.byId[REFERENCE_RELATES_TO].enabled = true;
    data.definitions.references.byId[REFERENCE_RELATES_TO].bidirectional = true;
    data.definitions.references.byId[REFERENCE_RELATES_TO].multiple = true;
    data.definitions.references.byId[REFERENCE_RELATES_TO].showInverse = false;
    data.definitions.references.byId[REFERENCE_RELATES_TO].canDelete = false;
  }

  for (let [key, value] of Object.entries(data.definitions.types.byId)) {
    if (data.definitions.references.byId[REFERENCE_RELATES_TO].types.indexOf(key) === -1) {
      data.definitions.references.byId[REFERENCE_RELATES_TO].types.push(key);
    }

    if (value.references.indexOf(REFERENCE_RELATES_TO) === -1) {
      value.references.push(REFERENCE_RELATES_TO);
    }
  }
};

const convertAnniversaryTypeToIcon = obj => {
  if (!obj.data || !obj.data.items) {
    return;
  }
  // Find any items that have anniversaryType and convert it to anniversaryIcon
  each(obj.data.items.byId, item => {
    if (item.anniversaryType) {
      item.anniversaryIcon = item.anniversaryType === "birthday" ? "markerPresent" : "markerOther";
    }
  });
};

const convertItemAnniversaryToType = obj => {
  if (!obj.definitions || !obj.definitions.types) {
    return;
  }
  // Fnd any "person" types and set type anniversary to Birthday
  each(obj.definitions.types.byId, type => {
    if (
      [itemTypeIds.PERSON_ITEM_TYPE_ID, "person", "character"].includes(type.id) ||
      ["Person", "Character"].includes(type.label)
    ) {
      type.anniversaryDisplayLabel = "Birthday";
      type.extendAnniversaryBeyondEnd = false;
    }
  });
};

// If we force adding the defaultNarrative type above, the allIdsForType list might not match
// So we need to make sure everything lines up correctly
const ensureAllIdsMatchesEnabledTypes = obj => {
  if (!obj.definitions || !obj.definitions.types || !obj.data || !obj.data.items) {
    return;
  }
  const allIdsKeys = keys(obj.data.items.allIdsForType);
  const typeKeys = filter(keys(obj.definitions.types.byId), id => obj.definitions.types.byId[id].enabled);

  // Removed:
  // While it might be a good idea to remove these values, this change triggered at least
  // two crash-style bugs in our in-built test files alone.
  // Both were caused by other things, but it highlights the risk of making this change
  // without a lot more testing given the number of end-user files that are out there in the
  // wild now.
  //
  // // Delete any existing keys that should not exist
  // each(allIdsKeys, id => {
  //   const t = obj.definitions.types.byId[id];
  //   if (!t || !t.enabled) {
  //     delete obj.data.items.allIdsForType[id];
  //   }
  // });

  // Add any keys that should exist
  each(typeKeys, id => {
    if (allIdsKeys.indexOf(id) < 0) {
      obj.data.items.allIdsForType[id] = [];
    }
  });
};

// There was a bug in the Entity List Panel that allowed items of the wrong type to be dropped
// into the allIdsForType list. Since that has been out in the wild in betas, we now need to repair
// any such instances.
const ensureAllIdsForTypeIsValid = obj => {
  if (!obj.data || !obj.data.items) {
    return;
  }
  each(obj.data.items.allIdsForType, (idList, typeId) => {
    obj.data.items.allIdsForType[typeId] = filter(idList, id => obj.data.items.byId[id].type === typeId);
  });
};

const removeDatesFromItemsThatCannotHaveDates = obj => {
  if (!obj.data || !obj.data.items) {
    return;
  }
  // Find any items that have anniversaryType and convert it to anniversaryIcon
  each(obj.data.items.byId, item => {
    const canHaveDate = obj.definitions.types.byId[item.type].canHaveDate;

    if (!canHaveDate) {
      delete item.startDate;
      delete item.duration;
      delete item.preferredStartDate;
      delete item.preferredDuration;
      delete item.latestStartDate;
      delete item.durationToEarliestEnd;
    }
  });
};

const convertHasAvatarToCompactDisplay = obj => {
  if (!obj.definitions || !obj.definitions.types) {
    return;
  }
  each(obj.definitions.types.byId, type => {
    if (isNil(type.compactDisplay)) {
      if (type.hasAvatar) {
        type.compactDisplay = compactDisplays.AVATAR_INITIALS;
      } else {
        type.compactDisplay =
          type.canIncludeInNarrative || type.showInSequence ? compactDisplays.IDENTIFIER : compactDisplays.SHORT_LABEL;
      }
    }
  });
};

const narrativeLabelIdForLabel = (labels, value) => {
  const obj = find(labels, l => l.label === value);
  return obj ? obj.id : uniqueId();
};

const convertNarrativeLabelsToObjects = obj => {
  // If there are no labels yet, we can leave it to use the defaults
  // So check if labels exist, and if they are strings that need to be updated
  if (
    obj.definitions &&
    obj.definitions.narrativeSettings &&
    obj.definitions.narrativeSettings.labels &&
    isString(obj.definitions.narrativeSettings.labels[0])
  ) {
    // First create a list of narrative labels... use default label IDs where possible
    const newLabels = obj.definitions.narrativeSettings.labels.map(label => {
      return {
        id: narrativeLabelIdForLabel(defaultFictionNarrativeLabels, label),
        label
      };
    });
    // Set newLabels in definitions
    obj.definitions.narrativeSettings.labels = newLabels;

    if (obj.data && obj.data.items) {
      const narrativeIds = treeUtils.getIdsInTree(obj.data.narrative);
      const narrativeIdsSet = Set(narrativeIds);

      // Now, update all items to change their narrativeLabel to the appropriate one.
      each(obj.data.items.byId, item => {
        if (!isNil(item.narrativeLabel) && narrativeIdsSet.has(item.id)) {
          item.narrativeLabel = newLabels[item.narrativeLabel].id;
        } else {
          delete item.narrativeLabel;
        }
      });
    }
  }
};

const upgradeBcDate = (calendar, oldDate) => {
  if (isDateValid(oldDate) && oldDate.era === 0) {
    const { era, year, month, day, hour, minute, second, precision } = oldDate;
    return dateWithComponents(calendar, era, year, month, day, hour, minute, second, precision).toObject();
  }
  return oldDate;
};

const upgradeDatesInObject = (calendar, obj, fieldNames) => {
  each(fieldNames, f => {
    obj[f] = upgradeBcDate(calendar, obj[f]);
  });
};

const fixOldGregorianCalendarBCDates = obj => {
  // First we need to work out if we need to upgrade the calendar
  // We will only upgrade the calendar if it matches the old gregorian calendar
  // so let's check that first.
  const oldCalendar = createFromJS.CalendarRecord(obj.definitions.calendar);
  if (!wasOldStyleGregorianCalendarFromCalendar(oldCalendar)) {
    return;
  }

  // If we reach here, we need to repair any BC dates
  // First, upgrade to the new style Gregorian Calendar
  obj.definitions.calendar.eras[0].hasLeapYears = true;
  obj.definitions.calendar.eras[0].leapOffset = 1;

  // Now get a copy of the newCalendar
  const newCalendar = createFromJS.CalendarRecord(obj.definitions.calendar);

  if (obj.data) {
    // Update the Items
    if (obj.data.items) {
      const itemDateFields = ["startDate", "latestStartDate", "preferredStartDate"];
      each(obj.data.items.byId, item => {
        upgradeDatesInObject(newCalendar, item, itemDateFields);
      });
    }

    // Update the Calendar Markers
    if (obj.data.calendarMarkers) {
      const markerDateFields = ["startDate", "repeatUntil"];
      each(obj.data.calendarMarkers.byId, marker => {
        upgradeDatesInObject(newCalendar, marker, markerDateFields);
      });
    }

    // Update the Constraints
    if (obj.data.dateConstraints) {
      const constraintDateFields = ["date"];
      each(obj.data.dateConstraints.byId, constraint => {
        upgradeDatesInObject(newCalendar, constraint, constraintDateFields);
      });
    }

    if (obj.data.timelineRange) {
      const timelineRangeDateFields = ["from", "to"];
      upgradeDatesInObject(newCalendar, obj.data.timelineRange, timelineRangeDateFields);
    }
  }
};

const defaultCategoryForTypeId = typeId => {
  if (typeId === itemTypeIds.PERSON_ITEM_TYPE_ID) {
    return itemTypeCategories.PERSON;
  }
  if (typeId === itemTypeIds.LOCATION_ITEM_TYPE_ID) {
    return itemTypeCategories.LOCATION;
  }
  return itemTypeCategories.OTHER;
};

const updateItemTypeCategories = obj => {
  if (!obj.definitions) {
    return;
  }
  each(obj.definitions.types.byId, (type, id) => {
    if (!has(type, "category") || isNil(type.category)) {
      type.category = defaultCategoryForTypeId(id);
    }
  });
};

const checkDependencies = obj => {
  if (obj.data && obj.data.dependencies) {
    const dependencies = obj.data.dependencies;
    const itemsById = obj.data.items.byId;
    const typesById = obj.definitions && obj.definitions.types ? obj.definitions.types.byId : null;

    dependencies.allIds.forEach(dId => {
      const dep = dependencies.byId[dId];
      // Both subject and object should be the item type that can have date
      const subjectItem = itemsById[dep.subject];
      const subjectItemType = typesById ? typesById[subjectItem.type] : null;
      const subjectCanHaveDate = !subjectItemType || subjectItemType.canHaveDate;
      const objectItem = itemsById[dep.object];
      const objectItemType = typesById ? typesById[objectItem.type] : null;
      const objectCanHaveDate = !objectItemType || objectItemType.canHaveDate;
      // Note: no type definitions assumes that the items can have dependencies
      if (subjectCanHaveDate && objectCanHaveDate) {
        // Make sure both of the items have the dependency value
        // Make sure no redundant
        if (!subjectItem.blockingDependencies.includes(dId)) {
          subjectItem.blockingDependencies.push(dId);
        }
        subjectItem.blockedByDependencies = subjectItem.blockedByDependencies.filter(id => id !== dId);

        if (!objectItem.blockedByDependencies.includes(dId)) {
          objectItem.blockedByDependencies.push(dId);
        }
        objectItem.blockingDependencies = objectItem.blockingDependencies.filter(id => id !== dId);
      } else {
        // Remove the dependencies
        dependencies.allIds = dependencies.allIds.filter(id => id !== dId);
        delete dependencies.byId[dId];
        subjectItem.blockingDependencies = subjectItem.blockingDependencies.filter(id => id !== dId);
        objectItem.blockedByDependencies = objectItem.blockedByDependencies.filter(id => id !== dId);
      }
    });
  }
};

const checkDateConstraints = obj => {
  if (obj.data && obj.data.dateConstraints) {
    const dateConstraints = obj.data.dateConstraints;
    const itemsById = obj.data.items.byId;
    const typesById = obj.definitions && obj.definitions.types ? obj.definitions.types.byId : null;

    // Check relative constraints
    dateConstraints.allIds.forEach(cId => {
      const constraint = dateConstraints.byId[cId];
      if (constraint.constraintType === ConstraintTypes.Relative) {
        // Both subject and object should be the item type that can have date
        const subjectItem = itemsById[constraint.subject];
        const subjectItemType = typesById ? typesById[subjectItem.type] : null;
        const subjectCanHaveDate = !subjectItemType || subjectItemType.canHaveDate;
        const objectItem = itemsById[constraint.object];
        const objectItemType = typesById ? typesById[objectItem.type] : null;
        const objectCanHaveDate = !objectItemType || objectItemType.canHaveDate;
        // Note: no type definitions assumes that the items can have date constraints
        if (subjectCanHaveDate && objectCanHaveDate) {
          // Make sure both of the items have the date constraint value
          if (!subjectItem.dateConstraints.includes(cId)) {
            subjectItem.dateConstraints.push(cId);
          }
          if (!objectItem.dateConstraints.includes(cId)) {
            objectItem.dateConstraints.push(cId);
          }
        } else {
          // Remove the date constraint
          dateConstraints.allIds = dateConstraints.allIds.filter(id => id !== cId);
          delete dateConstraints.byId[cId];
          subjectItem.dateConstraints = subjectItem.dateConstraints.filter(id => id !== cId);
          objectItem.dateConstraints = objectItem.dateConstraints.filter(id => id !== cId);
        }
      }
    });
  }
};

const checkAttachments = obj => {
  if (obj.data && obj.data.items) {
    // Fix a previous mistake may give a incorrect format for the item attachments
    // See https://aeontimeline.atlassian.net/browse/ATR-2496
    Object.values(obj.data.items.byId).forEach(i => {
      if (i.attachments && !isArray(i.attachments)) {
        i.attachments = [];
      }
    });
  }
};

const ensureDependenciesAndConstraintsExist = obj => {
  if (!obj.data) {
    return;
  }
  const itemsById = obj.data.items.byId;
  each(itemsById, item => {
    item.dateConstraints = filter(item.dateConstraints, cId => has(obj.data.dateConstraints.byId, cId));
    item.blockingDependencies = filter(item.blockingDependencies, dId => has(obj.data.dependencies.byId, dId));
    item.blockedByDependencies = filter(item.blockedByDependencies, dId => has(obj.data.dependencies.byId, dId));
  });
};

const removeSyncExternalRepAndStatus = obj => {
  if (obj.sync) {
    delete obj.sync.externalRep;
    delete obj.sync.status;
  }
};

const updateOldV3SyncSettings = obj => {
  // Whether or not "entityFolders" exists in SyncSettings is our proxy for determining whether
  // we need to update the old sync settings or not
  if (obj.sync && obj.sync.settings && obj.sync.settings.enabled && !has(obj.sync.settings, "entityFolders")) {
    // Need to update to new settings
    // We will default to using keywords for relationships since that is what the old version did
    obj.sync.settings.useKeywordsForRelationships = true;
    // Any fields that had folderId set now need to have that transferred to keywordTypes and entityFolders instead
    let keywordTypes = [];
    let entityFolders = {};
    let fields = {};
    each(obj.sync.settings.fields, f => {
      if (f.folderId) {
        keywordTypes.push(f.id);
        entityFolders[f.folderId] = {
          folderId: f.folderId,
          mainType: f.id,
          flattened: true,
          defaultDocumentType: f.id,
          defaultFolderType: f.id
        };
      } else {
        fields[f.id] = f;
      }
    });
    obj.sync.settings.entityFolders = fields;
    obj.sync.settings.entityFolders = entityFolders;
    obj.sync.settings.keywordTypes = keywordTypes;
  }
};

// A user managed to create an error with their file where they had a null
// in an allIds list. So we will test it and make sure we remove them if they
// are there.
const ensureIdsInAllIdsExist = obj => {
  if (obj.definitions) {
    obj.definitions.references.allIds = filter(obj.definitions.references.allIds, id => id);
    obj.definitions.types.allIds = filter(obj.definitions.types.allIds, id => id);
    obj.definitions.properties.allIds = filter(obj.definitions.properties.allIds, id => id);
  }
};

export default obj => {
  let result = obj ? Object.assign({}, obj) : obj;
  if (result) {
    const fileVersion = obj && obj.fileVersion ? obj.fileVersion : 0;

    // Fix the BC calendar, if it is the old format
    if (result.definitions && !has(result.definitions.calendar.leapCycles)) {
      fixOldGregorianCalendarBCDates(result);
    }

    ensureIdsInAllIdsExist(result);

    // sequenceViewSettings => subwayViewSettings
    if (result.definitions && result.definitions.sequenceViewSettings) {
      result.definitions = renameProp("sequenceViewSettings", "subwayViewSettings", result.definitions);
    }
    // AppState fixed:
    //   - sequenceOptions => subwayOptions
    //   - insert "narrative" into outline columns
    //   - insert typeIds into relationship options
    //   - remove All Events thread from subway options
    if (result.appState) {
      fixSequenceOptions(result, "desktopOpenViews");
      fixSequenceOptions(result, "tabletOpenViews");
      fixViewColumns(result, "desktopOpenViews");
      fixViewColumns(result, "tabletOpenViews");
      setRelationshipViewColumnTypeIds(result, "desktopOpenViews");
      setRelationshipViewColumnTypeIds(result, "tabletOpenViews");
      removeSubwayViewAllEventsThread(result, "desktopOpenViews");
      removeSubwayViewAllEventsThread(result, "tabletOpenViews");
      removeInvalidThreads(result, "desktopOpenViews");
      removeInvalidThreads(result, "tabletOpenViews");
      addExposedFilterTypes(result);
    }
    // Create global list for tags
    if (result.data && !result.data.tags) {
      createGlobalTags(result);
    }
    // Upgrade any property values from a List to a Map
    convertAllowedPropertyValuesToMap(result);
    // Changed from storing the item's anniversary type to just the icon
    convertAnniversaryTypeToIcon(result);
    // Moved anniversary labels from item definition to type defintion
    convertItemAnniversaryToType(result);

    // There was a bug that existed prior to 0.3.8.0 that allowed an item to not
    // have its children set correctly.
    // To fix that issue, anything with a file version prior to 6 may need to have
    // its children updated
    // (technically, this number could be much lower, but doesn't matter)
    if (fileVersion < 6) {
      // And force items to have children if they do not already!
      setItemChildren(result);
    }
    // Copy across item super and children into the separate data structure that
    // allows us to avoid calculation re-runs when the user changes irrelevant data
    appendSuperAndChildrenByIdToData(result);
    // Make sure the Narrative Folder item type actually has isNarrativeFolder set
    // ensureNarrativeFolderHasCorrectSetting(result);
    // Make sure the Narrative Folder item type actually has isNarrativeFolder set
    ensureNarrativeFolderHasCorrectSetting(result);
    // Make sure narrativeFolder exists
    ensureNarrativeFolderExists(result);
    // Make sure narrative is turned on if any item is narrative only type
    ensureIncludeNarrativeIsCorrect(result);
    // Make sure relatesToRef exists and all types reference it
    ensureRelatesToRefExists(result);
    // Also ensure that all items have a preferredPrecision
    setPreferredPrecision(result);
    // Make sure that allIdsForType has the correct typeIDs based on the template
    // after we have adjusted it above (e.g. adding narrative folder)
    ensureAllIdsMatchesEnabledTypes(result);
    // Make sure allIdsForType does not have any stray item IDs that do not belong there
    // (may have been introduced in bug when dragging Entity List)
    ensureAllIdsForTypeIsValid(result);
    // Make sure that types that are not allowed dates do not have dates
    removeDatesFromItemsThatCannotHaveDates(result);
    convertHasAvatarToCompactDisplay(result);
    // Convert string narrativeLabels to objects
    convertNarrativeLabelsToObjects(result);
    // Update the category for item types
    updateItemTypeCategories(result);

    // Repair the dependencies
    checkDependencies(result);

    // Repair the date constraints
    checkDateConstraints(result);

    // Repair the attachments
    checkAttachments(result);

    // Make sure dateConstraints and dependencies referenced in items exist properly
    ensureDependenciesAndConstraintsExist(result);

    // stop sending in the sync externalRepresentation
    // if we can't load it, then nothing should appear
    removeSyncExternalRepAndStatus(result);
    updateOldV3SyncSettings(result);
  }
  return result;
};
